import { es, type Messages } from "./es";

type Leaves<T, P extends string = ""> = {
  [K in keyof T & (string | number)]: T[K] extends string
    ? `${P}${K}`
    : T[K] extends object
      ? Leaves<T[K], `${P}${K}.`>
      : never;
}[keyof T & (string | number)];

export type MessageKey = Leaves<Messages>;

const messages: Messages = es;
export const locale = "es-ES";

/** t("stats.gainThisYear", { year: 2026 }) → "Desnivel 2026" */
export function t(key: MessageKey, params?: Record<string, string | number>): string {
  let v: unknown = messages;
  for (const part of key.split(".")) v = (v as Record<string, unknown>)?.[part];
  let s = typeof v === "string" ? v : key;
  if (params) for (const [k, val] of Object.entries(params)) s = s.replaceAll(`{${k}}`, String(val));
  return s;
}

/** Pluralised count helper for keys that have a "…One" variant. */
export function tCount(key: MessageKey, oneKey: MessageKey, n: number): string {
  return n === 1 ? t(oneKey) : t(key, { n: fmtInt(n) });
}

const intFmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
const decFmt = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
export const fmtInt = (n: number) => intFmt.format(n);
export const fmtDec = (n: number) => decFmt.format(n);
export const fmtM = (n: number | null | undefined) => (n == null ? "—" : `${intFmt.format(Math.round(n))} m`);

export function fmtDate(iso: string, style: "long" | "short" | "medium" = "medium"): string {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts: Intl.DateTimeFormatOptions =
    style === "long"
      ? { day: "numeric", month: "long", year: "numeric" }
      : style === "short"
        ? { day: "numeric", month: "short" }
        : { day: "numeric", month: "short", year: "numeric" };
  return new Intl.DateTimeFormat(locale, opts).format(d);
}

export function fmtDuration(min: number | null | undefined): string {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h} h${m ? ` ${m} min` : ""}` : `${m} min`;
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${decFmt.format(n / 1024)} KB`;
  if (n < 1024 ** 3) return `${decFmt.format(n / 1024 ** 2)} MB`;
  return `${decFmt.format(n / 1024 ** 3)} GB`;
}
